// Complete data states (UX04): loading, empty, error+retry, stale, unauthorized, partial. Owner: WP04.
import type { CSSProperties, ReactNode } from 'react';
import { CircleAlert, History, LoaderCircle, Plug, RefreshCw, TriangleAlert } from 'lucide-react';
import { ApiRequestError } from '../app/api.ts';
import { Button, ButtonLink } from './Button.tsx';
import { formatWhen } from './format.ts';

export function Skeleton({ width, height = 16, radius, className }: { width?: number | string; height?: number | string; radius?: number; className?: string }) {
  const style: CSSProperties = { width, height, borderRadius: radius };
  return <span className={['ui-skeleton', className ?? ''].filter(Boolean).join(' ')} style={style} aria-hidden />;
}

export function LoadingState({ label = 'Loading…', compact }: { label?: string; compact?: boolean }) {
  return (
    <div className={compact ? 'ui-state ui-state--compact' : 'ui-state'} role="status" aria-live="polite">
      <LoaderCircle className="ui-spin" size={24} aria-hidden />
      <p>{label}</p>
    </div>
  );
}

export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="ui-state">
      {icon ? <span className="ui-state__icon" aria-hidden>{icon}</span> : null}
      <h2 className="ui-state__title">{title}</h2>
      {body ? <p>{body}</p> : null}
      {action ? <div className="ui-state__actions">{action}</div> : null}
    </div>
  );
}

const AUTH_CODES = new Set(['UNAUTHORIZED', 'SESSION_EXPIRED', 'AUTH_INVALID', 'ACCOUNT_EXPIRED', 'ACCOUNT_DISABLED']);
/** True when the error means "reconnect", not "retry". */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiRequestError && AUTH_CODES.has(error.code);
}

export function ErrorState({ error, title = 'Something went wrong', onRetry }: { error: unknown; title?: string; onRetry?: () => void }) {
  const api = error instanceof ApiRequestError ? error : null;
  const message = api?.message ?? 'Tuned could not load this. Check your connection and try again.';
  return (
    <div className="ui-state" role="alert">
      <span className="ui-state__icon ui-state__icon--danger" aria-hidden><TriangleAlert size={28} /></span>
      <h2 className="ui-state__title">{title}</h2>
      <p>{message}</p>
      {onRetry ? <div className="ui-state__actions"><Button icon={<RefreshCw size={18} />} onClick={onRetry}>Try again</Button></div> : null}
      {api?.requestId ? <p className="ui-state__ref">Reference {api.requestId}{api.code ? ` · ${api.code}` : ''}</p> : null}
    </div>
  );
}

export function UnauthorizedState({ expired, message }: { expired?: boolean; message?: string }) {
  return (
    <div className="ui-state" role="alert">
      <span className="ui-state__icon" aria-hidden><Plug size={28} /></span>
      <h2 className="ui-state__title">{expired ? 'Reconnect to keep watching' : 'Connect your provider'}</h2>
      <p>{message ?? (expired ? 'Your provider session expired. Your saved favorites and history are still here.' : 'This device is not connected to a provider yet.')}</p>
      <div className="ui-state__actions"><ButtonLink to="/setup" variant="primary">{expired ? 'Reconnect' : 'Connect'}</ButtonLink></div>
    </div>
  );
}

/** Shown above preserved data when a refresh failed or the server marked it stale (STORE02, XC03). */
export function StaleBanner({ updatedAt, message, onRetry }: { updatedAt: string | number | null | undefined; message?: string; onRetry?: () => void }) {
  const when = updatedAt ? formatWhen(updatedAt) : null;
  return (
    <div className="ui-notice ui-notice--warn" role="status">
      <History size={18} aria-hidden />
      <p>{message ?? 'Showing saved information.'}{when ? ` Last updated ${when}.` : ''}</p>
      {onRetry ? <Button size="sm" variant="ghost" onClick={onRetry}>Refresh</Button> : null}
    </div>
  );
}

/** Some data is missing or still arriving (e.g. "Loaded 2,500 of 10,000 channels"). */
export function PartialDataNotice({ children }: { children: ReactNode }) {
  return (
    <div className="ui-notice" role="status">
      <CircleAlert size={18} aria-hidden />
      <p>{children}</p>
    </div>
  );
}

type QueryLike<T> = { data: T | undefined; error: unknown; isPending: boolean; isError: boolean; refetch(): unknown; dataUpdatedAt?: number };

/**
 * Renders the right UX04 state for a TanStack query: loading → error/unauthorized → empty → data.
 * When a refetch fails but earlier data exists, the data stays visible under a StaleBanner.
 */
export function QueryState<T>({ query, isEmpty, empty, loading, errorTitle, children }: {
  query: QueryLike<T>;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
  errorTitle?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{loading ?? <LoadingState />}</>;
  if (query.data === undefined) {
    if (isAuthError(query.error)) return <UnauthorizedState expired={(query.error as ApiRequestError).code !== 'UNAUTHORIZED'} message={(query.error as ApiRequestError).message} />;
    return <ErrorState error={query.error} title={errorTitle} onRetry={() => void query.refetch()} />;
  }
  if (isEmpty?.(query.data)) return <>{empty ?? null}</>;
  return (
    <>
      {query.isError ? <StaleBanner updatedAt={query.dataUpdatedAt || null} message="Couldn't refresh. Showing what was loaded earlier." onRetry={() => void query.refetch()} /> : null}
      {children(query.data)}
    </>
  );
}
